import React from 'react'

interface ColorSwatch {
  name: string
  value: string
  textColor: string
}

interface ColorPalette {
  name: string
  description: string
  colors: ColorSwatch[]
}

const ColorPalettePreview: React.FC = () => {
  const colorPalettes: ColorPalette[] = [
    {
      name: 'Primary',
      description: 'Purple - Main brand color',
      colors: [
        { name: '50', value: 'bg-primary-50', textColor: 'text-primary-800' },
        { name: '100', value: 'bg-primary-100', textColor: 'text-primary-800' },
        { name: '200', value: 'bg-primary-200', textColor: 'text-primary-800' },
        { name: '300', value: 'bg-primary-300', textColor: 'text-primary-50' },
        { name: '400', value: 'bg-primary-400', textColor: 'text-primary-50' },
        { name: '500', value: 'bg-primary-500', textColor: 'text-primary-50' },
        { name: '600', value: 'bg-primary-600', textColor: 'text-primary-50' },
        { name: '700', value: 'bg-primary-700', textColor: 'text-primary-50' },
        { name: '800', value: 'bg-primary-800', textColor: 'text-primary-50' },
        { name: '900', value: 'bg-primary-900', textColor: 'text-primary-50' },
      ],
    },
    {
      name: 'Accent',
      description: 'Deep Blue - Secondary brand color',
      colors: [
        { name: '50', value: 'bg-accent-50', textColor: 'text-accent-800' },
        { name: '100', value: 'bg-accent-100', textColor: 'text-accent-800' },
        { name: '200', value: 'bg-accent-200', textColor: 'text-accent-800' },
        { name: '300', value: 'bg-accent-300', textColor: 'text-accent-50' },
        { name: '400', value: 'bg-accent-400', textColor: 'text-accent-50' },
        { name: '500', value: 'bg-accent-500', textColor: 'text-accent-50' },
        { name: '600', value: 'bg-accent-600', textColor: 'text-accent-50' },
        { name: '700', value: 'bg-accent-700', textColor: 'text-accent-50' },
        { name: '800', value: 'bg-accent-800', textColor: 'text-accent-50' },
        { name: '900', value: 'bg-accent-900', textColor: 'text-accent-50' },
      ],
    },
    {
      name: 'Green',
      description: 'Deep Green - Success and positive actions',
      colors: [
        { name: '50', value: 'bg-green-50', textColor: 'text-green-800' },
        { name: '100', value: 'bg-green-100', textColor: 'text-green-800' },
        { name: '200', value: 'bg-green-200', textColor: 'text-green-800' },
        { name: '300', value: 'bg-green-300', textColor: 'text-green-50' },
        { name: '400', value: 'bg-green-400', textColor: 'text-green-50' },
        { name: '500', value: 'bg-green-500', textColor: 'text-green-50' },
        { name: '600', value: 'bg-green-600', textColor: 'text-green-50' },
        { name: '700', value: 'bg-green-700', textColor: 'text-green-50' },
        { name: '800', value: 'bg-green-800', textColor: 'text-green-50' },
        { name: '900', value: 'bg-green-900', textColor: 'text-green-50' },
      ],
    },
    {
      name: 'Gray',
      description: 'Cool Gray - UI elements and text',
      colors: [
        { name: '50', value: 'bg-gray-50', textColor: 'text-text-primary' },
        { name: '100', value: 'bg-gray-100', textColor: 'text-text-primary' },
        { name: '200', value: 'bg-gray-200', textColor: 'text-text-primary' },
        { name: '300', value: 'bg-gray-300', textColor: 'text-text-primary' },
        { name: '400', value: 'bg-gray-400', textColor: 'text-gray-50' },
        { name: '500', value: 'bg-gray-500', textColor: 'text-gray-50' },
        { name: '600', value: 'bg-gray-600', textColor: 'text-gray-50' },
        { name: '700', value: 'bg-gray-700', textColor: 'text-gray-50' },
        { name: '800', value: 'bg-gray-800', textColor: 'text-gray-50' },
        { name: '900', value: 'bg-gray-900', textColor: 'text-gray-50' },
      ],
    },
  ]

  const copyToClipboard = (colorClass: string) => {
    navigator.clipboard.writeText(colorClass)
    // You could add a toast notification here
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-avenir">
      <div className="mx-auto max-w-7xl">
        <header className="mb-16 text-center">
          <h1 className="text-4xl font-bold text-text-primary">App Color Palette</h1>
          <p className="mt-2 text-lg text-text-secondary">
            A visual reference for the application&pos;s updated color scheme
          </p>
        </header>

        <div className="space-y-12">
          {colorPalettes.map((palette) => (
            <div key={palette.name} className="overflow-hidden rounded-xl bg-surface shadow-lg">
              <div className="flex flex-col sm:flex-row">
                <div className="w-full bg-surface p-6 sm:w-1/4 md:w-1/5">
                  <h3 className="text-xl font-bold text-text-primary">{palette.name}</h3>
                  <p className="text-sm text-text-placeholder">{palette.description}</p>
                </div>
                <div className="grid flex-grow grid-cols-5 sm:grid-cols-10">
                  {palette.colors.map((color) => (
                    <div
                      key={color.name}
                      className={`group relative h-24 w-full cursor-pointer p-2 text-xs font-medium transition-all hover:scale-105 hover:shadow-lg sm:h-full ${color.value} ${color.textColor}`}
                      onClick={() => copyToClipboard(color.value)}
                      title={`Click to copy ${color.value}`}
                    >
                      <div className="flex h-full flex-col justify-end">
                        <span className="font-bold">{color.name}</span>
                        <span className="opacity-0 transition-opacity group-hover:opacity-100">
                          {color.value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 rounded-lg bg-surface p-6 shadow-lg">
          <h2 className="mb-4 text-2xl font-bold text-text-primary">Usage Examples</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-lg bg-primary p-4 text-white">
              <h3 className="font-bold">Primary Button</h3>
              <p className="text-sm opacity-90">bg-primary text-white</p>
            </div>
            <div className="rounded-lg border border-primary bg-surface p-4 text-primary">
              <h3 className="font-bold">Outlined Button</h3>
              <p className="text-sm opacity-70">border-primary text-primary</p>
            </div>
            <div className="rounded-lg bg-accent p-4 text-white">
              <h3 className="font-bold">Secondary Action</h3>
              <p className="text-sm opacity-90">bg-accent text-white</p>
            </div>
            <div className="rounded-lg bg-green p-4 text-white">
              <h3 className="font-bold">Success State</h3>
              <p className="text-sm opacity-90">bg-green text-white</p>
            </div>
            <div className="rounded-lg bg-gray-200 p-4 text-gray-800">
              <h3 className="font-bold">Neutral Background</h3>
              <p className="text-sm opacity-70">bg-gray-200 text-gray-800</p>
            </div>
            <div className="rounded-lg bg-gray-100 p-4 text-gray-700">
              <h3 className="font-bold">UI Element</h3>
              <p className="text-sm opacity-70">bg-gray-100 text-gray-700</p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-text-placeholder">
            Click on any color swatch to copy its Tailwind class to clipboard
          </p>
        </div>
      </div>
    </div>
  )
}

export default ColorPalettePreview
